/* eslint-disable max-len */
import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { PSClickWrap } from '@pactsafe/pactsafe-react-sdk';
import styles from './TermsAndConditions.module.css';
import { ClickWrapType, EPayContext, IBillingMethods } from '../../../../interfaces';
import { VENMO } from '../../../utils/constants';

export type TermsAndConditionsProps = {
  isFormValid: boolean;
  billingMethod: IBillingMethods;
  clickWrapConfig: ClickWrapType;
  hasRefill?: boolean;
  isPublicShop?: boolean;
};

const TERMS_AND_CONDITIONS_LINK =
  'https://vault.pactsafe.io/s/72097bf3-0831-44ad-b21b-744afe8e4a3d/legal.html?g=16266#terms-of-use';
const MEMBERSHIP_LINK = 'https://legal.fabfitfun.com/#box-membership-terms';
const SUBSCRIPTION_LINK = 'https://legal.fabfitfun.com/#refills-terms-conditions';

const TermsAndConditions: React.FC<TermsAndConditionsProps> = ({
  clickWrapConfig,
  billingMethod,
  isFormValid,
  hasRefill = false,
  isPublicShop = false,
}) => {
  const [disableClickWrapSending, setDisableClickWrapSending] = useState(false);
  const signerId = isFormValid ? clickWrapConfig.signerId : undefined;
  const isVenmoBilling = billingMethod === VENMO;

  const getTermsAndConditionsText = () => {
    if (isVenmoBilling) {
      return 'By clicking the Venmo button to checkout, you are agreeing to our';
    } else if (isPublicShop) {
      return 'By clicking "Place Order" you are agreeing to our';
    } else {
      return 'By clicking "Order the Box" you are agreeing to our';
    }
  };

  const isCheckboxChecked = useCallback(() => {
    const checkboxElement = document.getElementById(
      `ps-contract-checkbox-group-${clickWrapConfig?.groupId}`
    ) as HTMLInputElement;
    // As the Clickwrap component is hidden, this method retrieves the checkbox from the DOM and triggers a click event.
    if (clickWrapConfig?.signerId && checkboxElement && !checkboxElement.checked && isFormValid) {
      setTimeout(() => {
        checkboxElement.click();
        checkboxElement.disabled = true;
        setDisableClickWrapSending(true);
      }, 1000);
    }
    return checkboxElement;
  }, [clickWrapConfig?.groupId, clickWrapConfig?.signerId, isFormValid]);

  const memoizedIsCheckboxChecked = useMemo(() => isCheckboxChecked, [isCheckboxChecked]);

  useEffect(() => {
    if (isFormValid) {
      // If isFormValid is true it will automatically trigger the clickwrap acceptance
      memoizedIsCheckboxChecked();
    }
  }, [memoizedIsCheckboxChecked, isFormValid, isVenmoBilling]);

  return (
    <div className={styles.checkboxWrapper}>
      <div className={styles.termsAndConditions}>
        <p className={styles.terms} data-testid="terms-and-conditions">
          {getTermsAndConditionsText()}&nbsp;
          <a href={TERMS_AND_CONDITIONS_LINK} rel="noreferrer" target="_blank">
            Terms of Use and Sale
          </a>{' '}
          and&nbsp;
          <a href={MEMBERSHIP_LINK} rel="noreferrer" target="_blank">
            FabFitFun Membership Terms
          </a>
          {hasRefill && (
            <>
              &nbsp;and&nbsp;
              <a href={SUBSCRIPTION_LINK} rel="noreferrer" target="_blank">
                Subscription Terms and Conditions
              </a>
            </>
          )}
        </p>
      </div>
      <PSClickWrap
        signerId={signerId}
        accessId={clickWrapConfig.accessId}
        groupKey={clickWrapConfig.groupKey}
        snapshotLocation={clickWrapConfig.snapshotLocation}
        displayImmediately={false}
        disableSending={disableClickWrapSending}
        testMode={clickWrapConfig.testMode}
        dynamic
        clickWrapStyle="combined"
      />
    </div>
  );
};

export default TermsAndConditions;
